import { TranslationInfo } from "@shared/types/translation-info";
import { PROJECT_MODULE_TRANSLATIONS } from "../consts/project-modules.translation";

/**
 * Client-side/domain representation of a project module.
 *
 * It is derived from {@link ProjectModuleDto} when data is received from the API,
 * and converted back to {@link ProjectModuleDto} when sending data to the API.
 */
export interface ProjectModule {
  /**
   * Indicates whether the module is active.
   * A module is considered active when all of its ancestors are checked.
   */
  isActive: boolean;
  /** Indicates whether the module itself is checked. */
  isChecked: boolean;
  readonly id: string;
  translationInfo?: TranslationInfo | undefined;
  submodules?: ProjectModule[] | undefined;
}
/**
 * API representation (DTO) of a project module.
 *
 * This is the type used  when receiving data from the backend.
 * It should be mapped to {@link parseToProjectModule}
 */
export interface ProjectModuleDto {
  /**
   * Indicates whether the module is active.
   * A module is considered active when all of its ancestors are checked.
   */
  active: boolean;
  /** Indicates whether the module is checked */
  checked?: boolean;
  name: string;
  subModules?: ProjectModuleDto[] | undefined;
}
/**
 * API representation (DTO) of a project-module-update.
 *
 * This type derives from {@link ProjectModuleDto} but with only having minimal members needed by the API.
 * Use {@link parseToProjectModuleUpdateDto} to parse.
 */
export interface ProjectModuleUpdateDto extends Pick<ProjectModuleDto, "name" | "checked"> {
  subModules?: ProjectModuleUpdateDto[] | undefined;
}
export interface ProjectModulesUpdateDto {
  modules: ProjectModuleUpdateDto[];
}
/** Client-side/domain representation of a collection of project modules. */
export interface ProjectModules {
  auditInfo?: AuditInfo | undefined;
  modules: ProjectModule[];
}
/**
 * API representation (DTO) of a collection of project modules.
 *
 * This is the type used when receiving data from the backend.
 * It should be mapped to {@link ProjectModules} with {@link parseToProjectModules}
 */
export interface ProjectModulesDto {
  audit?: AuditInfo | undefined;
  modules: ProjectModuleDto[];
}
// TODO Global
/**
 * Audit metadata describing creation and last modification of a dataset.
 */
export interface AuditInfo {
  createdAt: Date;
  /** Name of the creator. */
  creator: string;
  modifiedAt: Date;
  /** Name of the last user or process that modified the dataset. */
  modifier: string;
  version: number;
}
/**
 * Parses a {@link ProjectModuleDto} to a {@link ProjectModule}.
 *
 * @param projectModuleDto Project module in API form.
 * @returns The parsed object
 */
export function parseToProjectModule (projectModuleDto: ProjectModuleDto): ProjectModule {
  return {
    isActive: projectModuleDto.active,
    isChecked: Boolean(projectModuleDto.checked),
    id: projectModuleDto.name,
    submodules: projectModuleDto.subModules?.map(parseToProjectModule),
    translationInfo: PROJECT_MODULE_TRANSLATIONS[ projectModuleDto.name ]
  };
}
/**
 * Parses a {@link ProjectModule} to a {@link ProjectModuleUpdateDto}.
 *
 * @param projectModule Project module in domain/client form.
 * @returns The parsed object
 */
export function parseToProjectModuleUpdateDto (projectModule: ProjectModule): ProjectModuleUpdateDto {
  return {
    checked: projectModule.isChecked,
    name: projectModule.id,
    subModules: projectModule.submodules?.map(parseToProjectModuleUpdateDto)
  };
}
/**
 * Maps {@link ProjectModulesDto} to {@link ProjectModules}.
 *
 * @param projectModulesDto Project Modules in API form.
 * @returns The parsed object
 */
export function parseToProjectModules (projectModulesDto: ProjectModulesDto): ProjectModules {
  return {
    auditInfo: projectModulesDto.audit,
    modules: projectModulesDto.modules.map(parseToProjectModule)
  };
}
export function parseToProjectModulesUpdateDto (projectModules: ProjectModules): ProjectModulesUpdateDto {
  return {
    modules: projectModules.modules.map(parseToProjectModuleUpdateDto)
  };
}
